import type {ChangeEvent, JSX} from "react";
import type {PaymentMethodField} from "../../../../domain/entities/PaymentMethod";
import {checkoutCopy} from "../../../i18n/checkout/es";
import styles from "../../../pages/checkout/CheckoutPage.module.css";

interface PaymentMethodFieldInputProps {
    field: PaymentMethodField;
    value: string;
    onChange: (fieldId: string, value: string) => void;
    labelClassName?: string;
}

export const PaymentMethodFieldInput = ({
    field,
    value,
    onChange,
    labelClassName,
}: PaymentMethodFieldInputProps) => {
    const inputId = `payment-field-${field.id}`;
    const isBankField = field.id === "bank";
    const bankOptions = field.options ?? [];
    const numericFieldIds = new Set(["dni_number", "phone_number"]);

    const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const nextValue =
            numericFieldIds.has(field.id) && "value" in event.target
                ? event.target.value.replace(/\D+/g, "")
                : event.target.value;

        onChange(field.id, nextValue);
    };

    const handleBankBlur = () => {
        if (!isBankField || bankOptions.length === 0) {
            return;
        }

        const normalized = (value ?? "").trim().toLowerCase();
        const exactMatch = bankOptions.find((option) => option.value.toLowerCase() === normalized);
        const nextValue = exactMatch ? exactMatch.value : bankOptions[0].value;

        if (nextValue !== value) {
            onChange(field.id, nextValue);
        }
    };

    const baseProps = {
        id: inputId,
        name: field.id,
        value,
        onChange: handleChange,
        required: field.required,
        className: styles.paymentInput,
        "aria-required": field.required,
    } as const;

    let inputElement: JSX.Element;

    if (isBankField && bankOptions.length) {
        const datalistId = `${inputId}-options`;

        inputElement = (
            <>
                <input
                    type="text"
                    {...baseProps}
                    placeholder={field.placeholder}
                    list={datalistId}
                    aria-autocomplete="list"
                    onBlur={handleBankBlur}
                />
                <datalist id={datalistId}>
                    {bankOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </datalist>
            </>
        );
    } else if (field.type === "select" && field.options?.length) {
        const placeholderOptionLabel = field.placeholder ?? checkoutCopy.payment.selectPlaceholder;

        inputElement = (
            <select {...baseProps}>
                <option value="" disabled>
                    {placeholderOptionLabel}
                </option>
                {field.options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        );
    } else {
        const typeAttribute =
            field.type === "email" || field.type === "tel" || field.type === "number" ? field.type : "text";
        const extraProps =
            field.id === "dni_number"
                ? ({
                    inputMode: "numeric" as const,
                    pattern: "[0-9]*",
                } satisfies Pick<React.InputHTMLAttributes<HTMLInputElement>, "inputMode" | "pattern">)
                : {};

        inputElement = (
            <input
                type={typeAttribute}
                {...baseProps}
                placeholder={field.placeholder}
                {...(field.pattern ? {pattern: field.pattern} : {})}
                {...extraProps}
            />
        );
    }

    return (
        <div className={styles.paymentField}>
            <label
                htmlFor={inputId}
                className={`${styles.paymentFieldLabel} ${labelClassName ? labelClassName : ""}`.trim()}
            >
                {field.label}
                {field.required ? " *" : ""}
            </label>
            {inputElement}
            {field.helperText && <p className={styles.paymentFieldHelper}>{field.helperText}</p>}
        </div>
    );
};
